import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Box, styled, Button, TextField, Tooltip, MenuItem, Pagination, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import SyncDisabledIcon from "@mui/icons-material/SyncDisabled";
import BugReport from "@mui/icons-material/BugReport";
import TimelineIcon from "@mui/icons-material/Timeline";

import { EditHeader, Settings, StudioSelection, TSMenu } from "./Studio";
import {
  useTestCaseResultsQuery,
  useRecentTestCaseResultsQuery,
  useTSRQuery,
  useAddStepQuery,
  useExecuteTCQuery,
  useProductQuery,
  useModuleQuery,
  useTestCaseQuery,
  useGetEnvQuery,
  useReplaceTestStepsMutation,
  useInlineTestStepsMutation,
  useDeleteTestStepsMutation,
  useCreateTestCaseMutation,
  useMergeCopilotQuery,
  useExecuteMobileTCQuery,
  useChangeToAssertMutation,
  useBulkEditFlow,
  useBulkEditWaitTime,
  useBulkEditKeywordAction,
  useDebugExecuteTCQuery,
  useBulkEditCondLogic,
} from "../useQuery";

import { ContextIcon, RefreshIcon, ExportIcon, VisiblityIcon } from "../components-new/icons";
import { CustomInput, IconContainer, PageTitle, TestItemCard } from "../components-new/common";
import EditTabs from "./Studio/EditTabs";
import {
  setEditItemIndex,
  setEditStep,
  setIsMobile,
  setSelectedComponent,
  setSelectedProduct,
  setSelectedTestCase,
  setSelectedTestCaseItem,
  setTestCases,
  setTsrData,
  setTabs,
} from "../Redux/TestAuthoring/TestAuthoring";
import useSaveStepQuery from "../useQuery/useSaveStepQuery";
import { useLocation, useNavigate } from "react-router-dom";
import { useItemWidth, useNotification } from "../hooks";
import CustomDialog from "../components-new/common/CustomDialog";
import { useQueryClient } from "@tanstack/react-query";
import { TEST_CASE_GENERATION_URL } from "../Config/Config";
import { OpenInNewOutlined } from "@mui/icons-material";
import TestTableView from "../components-new/common/TestTableView";
import ViewToggle from "../components-new/common/ViewToggle";
import { HelpIconButton } from "../components-new/help/help-modal";
import {
  AssertSubOptions,
  AssertTextOptions,
  CheckBoxOptions,
  DropDownOptions,
  KeywordData,
} from "./Studio/constants";
import { getActionOptions } from "./Studio/Edit/helper";
import DebugConfigModal from "./Studio/DebugConfigModal";
import { setAllTestSteps, clearExecutedSteps } from "../Redux/ExecutionReport/ExecutionReportSlice";
import ConditionalLogicModal from "../components-new/common/ConditionalLogicModal";
import ReviewFlowModal from "../components-new/common/ReviewFlowModal";
import TestCaseSelector from '../components-new/common/TestCaseSelector';

const StyledButton = styled(Button)`
  padding: 15px 28px;
  border-radius: 8px;
  text-transform: capitalize;
`;

const useTestCaseStack = () => {
  const [testCaseStack, setTestCaseStack] = useState([]);

  const addToStack = useCallback((testCase) => {
    setTestCaseStack((prevStack) => {
      // Only add if the test case is not already in the stack
      if (!prevStack.includes(testCase)) {
        return [...prevStack, testCase];
      }
      return prevStack;
    });
  }, []);

  const isInStack = useCallback(
    (testCase) => {
      return testCaseStack.includes(testCase);
    },
    [testCaseStack],
  );

  return { testCaseStack, addToStack, isInStack };
};

const TestStudio = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notify = useNotification();
  const queryClient = useQueryClient();
  const location = useLocation();
  const path = location.pathname;
  const RouteState = useLocation();
  const containerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const testStepsContainerRef = useRef(null);
  const itemsPerRow = 4;
  const gap = 32;
  const itemWidth = useItemWidth(containerRef, itemsPerRow, gap);

  const [refreshFlag, setRefreshFlag] = useState(true);
  const [editData, setEditData] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [open, setOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const [testCaseId, setTestCaseId] = useState("");
  const [testCaseSearchKeyword, setTestCaseSearchKeyword] = useState("");
  const [message, setMessage] = useState("");
  const [checkedItems, setCheckedItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssertModal, setShowAssertModal] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [showInlineModal, setShowInlineModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [testCaseName, setTestCaseName] = useState("");
  const [lastKnownRecording, setLastKnownRecording] = useState(null);
  const [dataa, setDataa] = useState(null);
  const [viewMode, setViewMode] = useState("table");

  const [showFlowModal, setShowFlowModal] = useState(false);
  const [showWaitTimeModal, setShowWaitTimeModal] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState("Must Pass");
  const [waitTime, setWaitTime] = useState("");
  const [waitType, setWaitType] = useState("Before");
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditValues, setBulkEditValues] = useState({
    keyword: "",
    action: "",
    subAction: "",
  });
  const selectedTestCase = useSelector((state) => state.authoringSlice.selectedTestCase);
  const selectedProduct = useSelector((state) => state.authoringSlice.selectedProduct);
  const selectedModule = useSelector((state) => state.authoringSlice.selectedComponent);
  const currentEditItemIndex = useSelector((state) => state.authoringSlice.editItemIndex);
  const [isProductSetInitially, setIsProductSetInitially] = useState(false);
  const { testCaseStack, addToStack, isInStack } = useTestCaseStack();
  const { data: products, refetch: refetchProductQuery } = useProductQuery();
  const { data: modules, refetch: refetchModuleQuery } = useModuleQuery(selectedProduct, {
    enabled: selectedProduct !== "" && selectedProduct !== 0,
  });
  // eslint-disable-next-line no-unused-vars
  const { data: envDetails, refetch: refetchEnvQuery } = useGetEnvQuery(selectedProduct);

  const {
    data: testCases,
    isLoadingTestcase,
    refetch: refetchTestCases,
  } = useTestCaseQuery(selectedModule, testCaseSearchKeyword, {
    enabled: selectedModule !== "" && selectedModule !== 0,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const {
    data: testCaseResults,
    isLoading,
    isSuccess,
  } = useTestCaseResultsQuery(selectedTestCase, {
    enabled: selectedTestCase !== "",
  });
  const { mutate: addStep, isSuccess: isAddSuccess } = useAddStepQuery();
  const { mutate: saveStep, isSuccess: isSaveSuccess } = useSaveStepQuery();
  const { mutate: executeTest, isSuccess: isExecuteSuccess } = useExecuteTCQuery();
  const { mutate: executeMobileTc, isSuccess: isExecuteMobileTcSuccess } =
    useExecuteMobileTCQuery();
  const { mutate: deleteTestSteps } = useDeleteTestStepsMutation();
  const { mutate: changeToAssert } = useChangeToAssertMutation();
  const { mutate: replaceTestSteps } = useReplaceTestStepsMutation({
    onSuccess: () => {
    notify("Test Steps Replaced Successfully", "success");
    queryClient.invalidateQueries(["testCaseList"]);
  },
  onError: () => {
    notify("Failed to Replace Test Steps", "error");
  },
  });
  const { mutate: inlineTestSteps } = useInlineTestStepsMutation({
    onSuccess: () => {
      notify("Test Steps Inlined Successfully", "success");
       queryClient.invalidateQueries(["testCaseList"]);
    },
    onError: () => {
      notify("Failed to Inline Test Steps", "error");
    },
  });
  const { mutate: createTestCase } = useCreateTestCaseMutation();

  const { mutate: bulkEditFlow } = useBulkEditFlow();
  const { mutate: bulkEditWaitTime } = useBulkEditWaitTime();
  const { mutate: bulkEditKeywordAction } = useBulkEditKeywordAction();

  const { data: latestRecordings } = useRecentTestCaseResultsQuery();

  const { data: tsrData } = useTSRQuery(selectedTestCase);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = testCaseResults?.data?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages = Math.ceil((testCaseResults?.data?.length || 0) / itemsPerPage);

  const isDebugMode = useSelector((state) => state.executionSlice.isDebugMode);
  const debugStepNumber = useSelector((state) => state.executionSlice.debugStepNumber);
  const debugMode = useSelector((state) => state.executionSlice.debugMode);
  const [showDebugModal, setShowDebugModal] = useState(false);

  const { mutate: debugExecuteTest } = useDebugExecuteTCQuery();

  const [showCondLogicModal, setShowCondLogicModal] = useState(false);

  const { mutate: bulkEditCondLogic } = useBulkEditCondLogic();

  const [showReviewFlowModal, setShowReviewFlowModal] = useState(false);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    if (testStepsContainerRef.current) {
      testStepsContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTestCase]);

  useEffect(() => {
    const testCaseFromResults = testCaseResults?.testcase?.Test_Case;
    const testCaseFromLatestRecordings = latestRecordings?.testcase?.Test_Case;

    if (testCaseFromResults) {
      addToStack(testCaseFromResults);
    }

    if (testCaseFromLatestRecordings) {
      if (!isInStack(testCaseFromLatestRecordings)) {
        addToStack(testCaseFromLatestRecordings);
        setLastKnownRecording(latestRecordings);
        dispatch(setSelectedProduct(latestRecordings.Product));
        dispatch(setSelectedComponent(latestRecordings.Modules));
        dispatch(setSelectedTestCase(latestRecordings.Testcase));

        setPageTitle(latestRecordings.testcase?.Test_Case);
        setTestCaseId(latestRecordings.testcase?.idtest_case);
        setTestCaseSearchKeyword(latestRecordings.testcase?.Test_Case);

        setRefreshFlag(true);
        refetchProductQuery();
        refetchModuleQuery();
        refetchTestCases();
      }
    }
  }, [
    testCaseResults,
    latestRecordings,
    addToStack,
    isInStack,
    dispatch,
    refetchProductQuery,
    refetchTestCases,
  ]);

  useEffect(() => {
    if (!pageTitle) return;

    setTestCaseSearchKeyword(pageTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (testCaseId) {
      setTestCaseSearchKeyword(testCaseId);
    }
  }, []);

  useEffect(() => {
    if (selectedModule) refetchTestCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModule]);

  useEffect(() => {
    refetchProductQuery();
    if (RouteState?.state?.fromExecutionReports) {
      setRefreshFlag(false);
      dispatch(setSelectedProduct(RouteState?.state?.idproducts));
      dispatch(setSelectedComponent(RouteState?.state?.idmodules));
      dispatch(setSelectedTestCase(RouteState?.state?.Test_Case));
      setTestCaseSearchKeyword(RouteState?.state?.Test_Case);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RouteState?.state, dispatch]);

  useEffect(() => {
    refetchModuleQuery();
  }, [refetchModuleQuery, selectedProduct]);

  useEffect(() => {
    setCheckedItems([]);
  }, [selectedTestCase]);

  useEffect(() => {
    if (isAddSuccess || isSaveSuccess) {
      setOpen(true);
      notify(message, "success");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveSuccess, isAddSuccess]);

  useEffect(() => {
    dispatch(setEditStep(""));
    dispatch(setEditItemIndex(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (testCaseResults) {
      dispatch(setTestCases(testCaseResults.data ?? []));
      if (testCaseResults.tabs) {
        dispatch(setTabs(testCaseResults.tabs));
      }
    }
  }, [dispatch, testCaseResults]);

  useEffect(() => {
    dispatch(setTsrData(tsrData));
  }, [dispatch, tsrData]);

  useEffect(() => {
    if (currentEditItemIndex != null) {
      const currentEditItem = testCaseResults?.data[currentEditItemIndex];

      setEditData(currentEditItem);
      dispatch(setEditStep(currentEditItem));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditItemIndex]);

  useEffect(() => {
    if (!selectedProduct && latestRecordings && !isProductSetInitially) {
      dispatch(setSelectedProduct(latestRecordings.Product));
      dispatch(setSelectedComponent(latestRecordings.Modules));
      dispatch(setSelectedTestCase(latestRecordings.Testcase));
      setPageTitle(latestRecordings?.testcase?.Test_Case);
      setTestCaseId(latestRecordings.testcase?.idtest_case);
      setIsProductSetInitially(true);
      setTestCaseSearchKeyword(latestRecordings.testcase.Test_Case);
    }
  }, [latestRecordings, dispatch, selectedProduct, isProductSetInitially]);

  useEffect(() => {
    if (isSuccess) {
      setPageTitle(testCaseResults.testcase.Test_Case);
      setTestCaseId(testCaseResults.testcase.idtest_case);
      dispatch(setSelectedTestCaseItem(testCaseResults.testcase));
      dispatch(setIsMobile(testCaseResults?.testcase?.case_Type === "MOBILE"));
    }
  }, [dispatch, isSuccess, selectedProduct, testCaseResults]);

  useEffect(() => {
    if (isExecuteSuccess || isExecuteMobileTcSuccess)
      navigate("/new/reports", {
        state: {
          isExecuteClick: true,
          fromTestStudio: true,
          idproducts: selectedProduct,
          idmodules: selectedModule,
          idtest_case: selectedTestCase,
          Test_Case: decodeURI(testCaseResults.testcase.Test_Case),
        },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExecuteSuccess, isExecuteMobileTcSuccess]);

  useEffect(() => {
    if (currentEditItemIndex === null && scrollPositionRef.current > 0) {
      if (testStepsContainerRef.current) {
        testStepsContainerRef.current.scrollTo({
          top: scrollPositionRef.current,
          behavior: "instant",
        });
      }
    }
  }, [currentEditItemIndex]);

  useEffect(() => {
    if (testCaseResults?.data) {
      dispatch(setAllTestSteps(testCaseResults.data));
      dispatch(clearExecutedSteps());
    }
  }, [dispatch, testCaseResults?.data]);

  const getNextNum = (testCaseResults, currentStepNum) => {
    if (!testCaseResults || !testCaseResults?.stepnums) return null;
    const { stepnums } = testCaseResults;
    const currentSeq = Object.entries(stepnums).find(
      ([seq, stepNum]) => stepNum === currentStepNum,
    )?.[0];
    const nextSeq = currentSeq ? String(Number(currentSeq) + 1) : null;
    return stepnums[nextSeq] ?? currentStepNum;
  };

  const handleEdit = (item) => {
    setRefreshFlag(false);
    const currenItemIndex = testCaseResults.data.findIndex(
      (testCaseItem) => testCaseItem.Step_Number === item.Step_Number,
    );
    if (testStepsContainerRef.current) {
      scrollPositionRef.current = testStepsContainerRef.current.scrollTop;
    }
    dispatch(setEditItemIndex(currenItemIndex));
  };

  const nextNum = testCaseResults ? getNextNum(testCaseResults, editData?.Step_Number) : null;
  const data = {
    tcid: editData?.Test_Case_Id,
    stepNumber: editData?.Step_Number,
    nextStepNumber: nextNum,
    tcname: testCaseResults?.testcase?.Test_Case,
    envurl: encodeURIComponent(testCaseResults?.data[0]?.TestData),
  };

  const { mutate: modalExecute } = useMergeCopilotQuery(data, {});

  const handleMerge = () => {
    modalExecute(data);
    //close the menu
  };

  const handleExecuteTest = (isBaseline) => {
    try {
      const selectedItem = testCaseResults.testcase;

      if (selectedItem.case_Type === "MOBILE") {
        executeMobileTc({
          Test_Case_Id: selectedItem.idtest_case,
          isDebug: isDebugMode,
          debugStepNumber: debugStepNumber,
          debugMode: debugMode,
        });
        //navigate user to reports page
        navigate("/new/reports", {
          state: {
            isExecuteClick: true,
            fromTestStudio: true,
            idproducts: selectedProduct,
            idmodules: selectedModule,
            idtest_case: selectedItem.idtest_case,
            Test_Case: decodeURI(selectedItem.Test_Case),
            isDebug: isDebugMode,
            debugStepNumber: debugStepNumber,
            debugMode: debugMode,
          },
        });
        return;
      }

      const obj = {
        id: selectedItem.idtest_case,
        SelectEnvironmentName: selectedItem.envname,
        SelectEnvironmentUrl: selectedItem.envurl,
        SelectBrowserName: selectedItem.browser,
        SelectResolution: selectedItem.SelectResolution,
        SelectRunonce: selectedItem.multirun,
        TcThreshold: selectedItem.testcasethreshold,
        testcase: selectedItem?.Test_Case,
        proxyurl: selectedItem.proxyurl,
        baseline: isBaseline,
        isDebug: isDebugMode,
        debugStepNumber: debugStepNumber,
        debugMode: debugMode,
      };

      executeTest(obj);
    } catch (error) {
      console.error("Error executing test:", error);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const addStepQuery = (payload) => {
    setMessage("Step added successfully");
    addStep(payload);
  };
  const saveStepQuery = (payload) => {
    setMessage("Step updated successfully");
    saveStep(payload);
  };

  const handleCheckboxChange = (e, item) => {
    if (e.target.checked) {
      setCheckedItems((prev) => [...prev, item]);
    } else {
      setCheckedItems((prev) => prev.filter((testStep) => testStep !== item));
    }
  };

  const handleDeleteTestSteps = () => {
    if (!checkedItems.length) {
      notify("Please select steps to delete...", "warning");
      return;
    } else {
      setShowDeleteModal(true);
    }
  };

  const handleChangeToAssert = () => {
    if (!checkedItems.length) {
      notify("Please select steps to Change to Assertion", "warning");
      return;
    } else {
      setShowAssertModal(true);
    }
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };
  const handleReplaceTestSteps = () => {
    if (!checkedItems.length) {
      notify("Please select steps to be replaced...", "warning");
      return;
    } else {
      setShowReplaceModal(true);
    }
  };

  const handleInlineTestSteps = () => {
    if (!checkedItems.length) {
      notify("Please select steps to be used for creating inline dependence...", "warning");
      return;
    } else {
      setShowInlineModal(true);
    }
  };

  const handleCreateTestCase = () => {
    if (!checkedItems.length) {
      notify("Please select steps to create...", "warning");
      return;
    } else {
      setShowCreateModal(true);
    }
  };

  const handleBulkEditKeywordAction = () => {
    if (!checkedItems.length) {
      notify("Please select steps to edit", "warning");
      return;
    }
    setShowBulkEditModal(true);
  };

  const handleBulkEditSave = () => {
    const data = {
      testStepIds: checkedItems.map((item) => item.idtest_step),
    };

    bulkEditKeywordAction(
      {
        testCaseId: selectedTestCase,
        data,
        Keyword: bulkEditValues.keyword,
        Action: bulkEditValues.action,
        subAction: bulkEditValues.subAction,
      },
      {
        onSuccess: () => {
          notify("Keyword & Action updated successfully", "success");
          setShowBulkEditModal(false);
          setCheckedItems([]);
        },
        onError: (error) => {
          notify(`Error updating Keyword & Action: ${error.message}`, "error");
        },
      },
    );
  };

  const handleFlowSave = () => {
    const testCaseId = selectedTestCase;
    const data = {
      testStepIds: checkedItems.map((item) => item.idtest_step),
    };

    bulkEditFlow(
      { testCaseId, data, flow: selectedFlow },
      {
        onSuccess: () => {
          notify(`Flow status updated to ${selectedFlow} successfully`, "success");
          setShowFlowModal(false);
          setCheckedItems([]);
        },
        onError: (error) => {
          notify(`Error updating flow status: ${error.message}`, "error");
          setShowFlowModal(false);
        },
      },
    );
  };

  const handleWaitTimeSave = () => {
    const testCaseId = selectedTestCase;
    const data = {
      testStepIds: checkedItems.map((item) => item.idtest_step),
    };

    bulkEditWaitTime(
      { testCaseId, waittime: waitTime, wait: waitType, data },
      {
        onSuccess: () => {
          notify(`Wait time updated successfully`, "success");
          setShowWaitTimeModal(false);
          setCheckedItems([]);
          setWaitTime("");
        },
        onError: (error) => {
          notify(`Error updating wait time: ${error.message}`, "error");
          setShowWaitTimeModal(false);
        },
      },
    );
  };

  const handleSetFlow = () => {
    if (!checkedItems.length) {
      notify("Please select steps to set flow status", "warning");
      return;
    }
    setShowFlowModal(true);
  };

  const handleSetWaitTime = () => {
    if (!checkedItems.length) {
      notify("Please select steps to set wait time", "warning");
      return;
    }
    setShowWaitTimeModal(true);
  };

  const handleSetCondLogic = () => {
    if (!checkedItems.length) {
      notify("Please select steps to set conditional logic", "warning");
      return;
    }
    setShowCondLogicModal(true);
  };

  const handleCondLogicSave = (values) => {
    const testCaseId = selectedTestCase;
    const data = {
      testStepIds: checkedItems.map((item) => item.idtest_step),
    };

    bulkEditCondLogic(
      {
        testCaseId,
        data,
        condLogic: values.condLogic,
        condexp: values.condexp,
      },
      {
        onSuccess: () => {
          notify("Conditional logic updated successfully", "success");
          setCheckedItems([]);
        },
        onError: (error) => {
          notify(`Error updating conditional logic: ${error.message}`, "error");
        },
      },
    );
  };

  const FlowAction = () => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            select
            label="Flow Status"
            value={selectedFlow}
            onChange={(e) => setSelectedFlow(e.target.value)}
            fullWidth
            variant="outlined"
          >
            <MenuItem value="Negative">Must Fail</MenuItem>
            <MenuItem value="Positive">Must Pass</MenuItem>
            <MenuItem value="Optional">Optional</MenuItem>
            <MenuItem value="Disable">Disable</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="text"
            sx={{ color: "#ff5336" }}
            onClick={() => {
              setCheckedItems([]);
              setShowFlowModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              background: "#ff5336",
              "&:hover": {
                background: "#e94a2f",
              },
            }}
            onClick={handleFlowSave}
          >
            Save
          </Button>
        </Box>
      </Box>
    );
  };

  const WaitTimeAction = () => {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <TextField
            select
            label="Wait Type"
            value={waitType}
            onChange={(e) => setWaitType(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
          >
            <MenuItem value="Before">Wait Before</MenuItem>
            <MenuItem value="After">Wait After</MenuItem>
            <MenuItem value="WaitTill">Wait Till Found</MenuItem>
            <MenuItem value="WaitForAllApi">Wait For All API</MenuItem>
            <MenuItem value="WaitForConfiguredApis">Wait For Configured APIs</MenuItem>
          </TextField>

          <TextField
            label="Enter Wait Time"
            variant="outlined"
            fullWidth
            type="number"
            value={waitTime}
            onChange={(e) => setWaitTime(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                e.preventDefault();
                const newValue =
                  e.key === "ArrowUp"
                    ? (parseInt(waitTime || "0") + 1).toString()
                    : (parseInt(waitTime || "0") - 1).toString();
                setWaitTime(newValue);
              }
            }}
          />
        </Box>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCheckedItems([]);
            setShowWaitTimeModal(false);
          }}
        >
          Cancel
        </Button>
        <Button variant="contained" sx={{ background: "#ff5336" }} onClick={handleWaitTimeSave}>
          Save
        </Button>
      </>
    );
  };

  const BulkEditAction = () => {
    const actionOptions = getActionOptions(bulkEditValues.keyword);

    const showSubActionDropdown = () => {
      if (!bulkEditValues.keyword || !bulkEditValues.action) return false;

      const keyword = bulkEditValues.keyword;
      const action = bulkEditValues.action;

      return (
        (keyword === "Assertion" && action === "Properties") ||
        (keyword === "Assertion" && action === "validatePartial") ||
        (keyword === "Validation" && action === "validatePartial") ||
        (keyword === "DropDown" && action === "Select") ||
        (keyword === "Check Box" && action === "Checkbox") ||
        (keyword === "Alert" && action === "Alert")
      );
    };

    const getSubActionOptions = () => {
      const keyword = bulkEditValues.keyword;
      const action = bulkEditValues.action;

      if (keyword === "Assertion" && action === "Properties") return AssertSubOptions;
      if ((keyword === "Assertion" || keyword === "Validation") && action === "validatePartial")
        return AssertTextOptions;
      if (keyword === "DropDown" && action === "Select") return DropDownOptions;
      if (keyword === "Check Box" && action === "Checkbox") return CheckBoxOptions;
      if (keyword === "Alert" && action === "Alert") return AssertTextOptions;

      return [];
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2 }}>
        <TextField
          select
          fullWidth
          label="Element"
          value={bulkEditValues.keyword}
          onChange={(e) =>
            setBulkEditValues({
              ...bulkEditValues,
              keyword: e.target.value,
              action: "",
              subAction: "",
            })
          }
        >
          {KeywordData.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label="Action"
          value={bulkEditValues.action}
          onChange={(e) =>
            setBulkEditValues({
              ...bulkEditValues,
              action: e.target.value,
              subAction: "",
            })
          }
          disabled={!bulkEditValues.keyword}
        >
          {actionOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {showSubActionDropdown() && (
          <TextField
            select
            fullWidth
            label="Options"
            value={bulkEditValues.subAction}
            onChange={(e) =>
              setBulkEditValues({
                ...bulkEditValues,
                subAction: e.target.value,
              })
            }
          >
            {getSubActionOptions().map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
          <Button
            sx={{ color: "#ff5336" }}
            onClick={() => {
              setCheckedItems([]);
              setShowBulkEditModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: "#ff5336" }}
            onClick={handleBulkEditSave}
            disabled={!bulkEditValues.keyword || !bulkEditValues.action}
          >
            Save
          </Button>
        </Box>
      </Box>
    );
  };

  const DeleteAction = () => {
    return (
      <>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCheckedItems([]);
            setShowDeleteModal(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ background: "#ff5336" }}
          onClick={() => {
            deleteTestSteps({
              data: checkedItems.map((item) => item.idtest_step),
              params: {
                testCaseId: selectedTestCase,
              },
            });
            setCheckedItems([]);
            setShowDeleteModal(false);
          }}
        >
          Delete
        </Button>
      </>
    );
  };

  const AssertAction = () => {
    return (
      <>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCheckedItems([]);
            setShowAssertModal(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ background: "#ff5336" }}
          onClick={() => {
            changeToAssert({
              data: checkedItems.map((item) => item.idtest_step),
              params: {
                testCaseId: selectedTestCase,
              },
            });
            setCheckedItems([]);
            setShowAssertModal(false);
          }}
        >
          Change to Assert
        </Button>
      </>
    );
  };

  const ReplaceAction = () => {
    const [selectedTestCase, setSelectedTestCase] = useState(null);

    const handleReplaceClick = () => {
      if (!selectedTestCase) {
        notify("Please select a test case", "warning");
        return;
      }
      
      replaceTestSteps({
        data: checkedItems.map((item) => item.idtest_step),
        params: {
          testCaseId: selectedTestCase.idtest_case,
          replaceTcId: selectedTestCase.idtest_case,
        }
      });
      setCheckedItems([]);
      setShowReplaceModal(false);
    };

    return (
      <>
        <TestCaseSelector 
          onTestCaseSelect={setSelectedTestCase}
          initialValues={{
            productId: selectedProduct,
            moduleId: selectedModule
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <Button
            sx={{ color: "#ff5336" }}
            onClick={() => {
              setCheckedItems([]);
              setShowReplaceModal(false);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            sx={{ background: "#ff5336" }} 
            onClick={handleReplaceClick}
            disabled={!selectedTestCase}
          >
            Replace
          </Button>
        </Box>
      </>
    );
  };

  const InlineAction = () => {
    const [selectedTestCase, setSelectedTestCase] = useState(null);

    const handleInlineClick = () => {
      if (!selectedTestCase) {
        notify("Please select a test case", "warning");
        return;
      }

      inlineTestSteps({
        data: checkedItems.map((item) => item.idtest_step),
        params: {
          testCaseId: selectedTestCase.idtest_case,
          inlineTcId: selectedTestCase.idtest_case,
        },
      });
      setCheckedItems([]);
      setShowInlineModal(false);
    };

    return (
      <>
        <TestCaseSelector 
          onTestCaseSelect={setSelectedTestCase}
          initialValues={{
            productId: selectedProduct,
            moduleId: selectedModule
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <Button
            sx={{ color: "#ff5336" }}
            onClick={() => {
              setCheckedItems([]);
              setShowInlineModal(false);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            sx={{ background: "#ff5336" }} 
            onClick={handleInlineClick}
            disabled={!selectedTestCase}
          >
            Replace Inline Test Case
          </Button>
        </Box>
      </>
    );
  };

  const CreateAction = () => {
    return (
      <>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setCheckedItems([]);
            setShowCreateModal(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ background: "#ff5336" }}
          onClick={() => {
            createTestCase({
              data: {
                testCaseName: testCaseName,
                testSteps: checkedItems,
                testCaseId: selectedTestCase,
              },
            });
            setCheckedItems([]);
            setShowCreateModal(false);
          }}
        >
          Create
        </Button>
      </>
    );
  };

  const handleDebugExecute = (debugConfig) => {
    try {
      const selectedItem = testCaseResults.testcase;

      if (selectedItem.case_Type === "MOBILE") {
        executeMobileTc({
          Test_Case_Id: selectedItem.idtest_case,
          isDebug: debugConfig.isDebugMode,
          debugStepNumber: debugConfig.debugStepNumber,
          debugMode: debugConfig.debugMode,
          option: debugConfig.option,
          idtest_step: debugConfig.idtest_step,
        });
        navigate("/new/reports", {
          state: {
            isExecuteClick: true,
            fromTestStudio: true,
            idproducts: selectedProduct,
            idmodules: selectedModule,
            idtest_case: selectedItem.idtest_case,
            Test_Case: decodeURI(selectedItem.Test_Case),
            isDebug: debugConfig.isDebugMode,
            debugStepNumber: debugConfig.debugStepNumber,
            debugMode: debugConfig.debugMode,
            option: debugConfig.option,
            idtest_step: debugConfig.idtest_step,
          },
        });
        return;
      }

      const obj = {
        id: selectedItem.idtest_case,
        SelectEnvironmentName: selectedItem.envname,
        SelectEnvironmentUrl: selectedItem.envurl,
        SelectBrowserName: selectedItem.browser,
        SelectResolution: selectedItem.SelectResolution,
        SelectRunonce: selectedItem.multirun,
        TcThreshold: selectedItem.testcasethreshold,
        testcase: selectedItem?.Test_Case,
        proxyurl: selectedItem.proxyurl,
        baseline: false,
        isDebug: debugConfig.isDebugMode,
        debugStepNumber: debugConfig.debugStepNumber,
        debugMode: debugConfig.debugMode,
        option: debugConfig.option,
        idtest_step: debugConfig.idtest_step,
      };

      debugExecuteTest(obj, {
        onSuccess: () => {
          navigate("/new/reports", {
            state: {
              isExecuteClick: true,
              fromTestStudio: true,
              idproducts: selectedProduct,
              idmodules: selectedModule,
              idtest_case: selectedItem.idtest_case,
              Test_Case: decodeURI(selectedItem.Test_Case),
              isDebug: debugConfig.isDebugMode,
              debugStepNumber: debugConfig.debugStepNumber,
              debugMode: debugConfig.debugMode,
              option: debugConfig.option,
              idtest_step: debugConfig.idtest_step,
            },
          });
        },
      });
    } catch (error) {
      console.error("Error executing test in debug mode:", error);
    }
  };

  const handleInlineTestCaseView = (testCaseId) => {
    dispatch(setSelectedTestCase(testCaseId));
    setTestCaseSearchKeyword(testCaseId);
    setRefreshFlag(false);
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <CustomDialog
        title={"Are you sure you want to delete selected test steps?"}
        open={showDeleteModal}
        actions={<DeleteAction />}
      />
      <CustomDialog
        title={"Are you sure you want to Create Inline Dependence for selected test steps?"}
        open={showInlineModal}
        actions={<InlineAction />}
      />
      <CustomDialog
        title={"Are you sure you want to Replace selected test steps?"}
        open={showReplaceModal}
        actions={<ReplaceAction />}
      />
      <CustomDialog
        title={"Are you sure you want to Change selected test steps to Assert?"}
        open={showAssertModal}
        actions={<AssertAction />}
      />

      <CustomDialog
        title={"Create Test Case"}
        open={showCreateModal}
        actions={<CreateAction />}
        content={
          <Box>
            <CustomInput
              labelinput="Test Case Name"
              placeholder="Enter test case name"
              onChange={(e) => setTestCaseName(e.target.value)}
              value={testCaseName}
            />
          </Box>
        }
      />
      <CustomDialog
        title={"Set Flow Status for Selected Test Steps"}
        open={showFlowModal}
        actions={<FlowAction />}
      />

      <CustomDialog
        title={"Set Wait Time for Selected Test Steps"}
        open={showWaitTimeModal}
        actions={<WaitTimeAction />}
      />

      <CustomDialog
        title={"Bulk Edit Keyword & Action"}
        open={showBulkEditModal}
        actions={<BulkEditAction />}
      />
      <ConditionalLogicModal
        open={showCondLogicModal}
        onClose={() => {
          setShowCondLogicModal(false);
          setCheckedItems([]);
        }}
        onSave={handleCondLogicSave}
      />
      <DebugConfigModal
        open={showDebugModal}
        onClose={() => setShowDebugModal(false)}
        onExecute={handleDebugExecute}
        testSteps={testCaseResults?.data || []}
        initialConfig={{
          isDebugMode,
          debugStepNumber,
          debugMode,
        }}
      />
      <ReviewFlowModal
        open={showReviewFlowModal}
        onClose={() => setShowReviewFlowModal(false)}
        tcid={selectedTestCase}
        latestRecordings={latestRecordings}
      />
      {currentEditItemIndex === null && (
        <>
          <Box mb={2} display="flex" alignItems="center">
            <PageTitle title={pageTitle} variant="h4" />
            <Typography variant="h6" color="#8089A8" fontSize="13px" fontWeight={500}>{testCaseId}</Typography>

            <Box
              display={"flex"}
              columnGap={"1rem"}
              alignItems="center"
              flex={1}
              justifyContent="flex-end"
            >
              <HelpIconButton path={path} />
              <Settings />
              <ViewToggle view={viewMode} onViewChange={handleViewChange} />

              <Box>
                {refreshFlag ? (
                  <IconContainer
                    handleClick={() => {
                      setRefreshFlag(false);
                      clearInterval(window.ExecuteTimer);
                    }}
                  >
                    <SyncDisabledIcon />
                  </IconContainer>
                ) : (
                  <IconContainer
                    handleClick={() => {
                      setRefreshFlag(true);
                    }}
                  >
                    <RefreshIcon />
                  </IconContainer>
                )}
              </Box>

              <TSMenu
                handleExecuteTest={handleExecuteTest}
                anchorEl={anchorEl}
                handleClose={handleMenuClose}
                setTestCaseSearchKeyword={setTestCaseSearchKeyword}
                handleDeleteTestSteps={handleDeleteTestSteps}
                handleInlineTestSteps={handleInlineTestSteps}
                handleReplaceTestSteps={handleReplaceTestSteps}
                handleCreateTestCase={handleCreateTestCase}
                handleChangeToAssert={handleChangeToAssert}
                handleSetFlow={handleSetFlow}
                handleSetWaitTime={handleSetWaitTime}
                handleBulkEditKeywordAction={handleBulkEditKeywordAction}
                handleSetCondLogic={handleSetCondLogic}
              />
              <Box onClick={handleMenuClick}>
                <IconContainer>
                  <ContextIcon />
                </IconContainer>
              </Box>
              <StyledButton
                disabled={!selectedTestCase}
                variant="contained"
                endIcon={<VisiblityIcon />}
                onClick={() => {
                  navigate("/new/reports", {
                    state: {
                      fromTestStudio: true,
                      idproducts: selectedProduct,
                      idmodules: selectedModule,
                      idtest_case: selectedTestCase,
                      Test_Case: testCaseResults.testcase.Test_Case,
                    },
                  });
                }}
                sx={{
                  backgroundColor: "#2550d1",
                  boxShadow: "0px 16px 30.6px 0px rgba(37, 80, 209, 0.27)",
                  width: "229px",
                }}
              >
                Execution Reports
              </StyledButton>
              <StyledButton
                variant="contained"
                color="primary"
                onClick={() => setShowReviewFlowModal(true)}
                endIcon={<TimelineIcon />}
                sx={{
                  backgroundColor: "#2550d1",
                  boxShadow: "0px 16px 30.6px 0px rgba(37, 80, 209, 0.27)",
                }}
              >
                Review Flow
              </StyledButton>
              <StyledButton
                variant="contained"
                color="warning"
                onClick={() => setShowDebugModal(true)}
                endIcon={<BugReport />}
                sx={{
                  backgroundColor: isDebugMode ? "#2550d1" : "#ff5336",
                  boxShadow: "0px 16px 30.6px 0px rgba(255, 83, 54, 0.27)",
                }}
              >
                {isDebugMode ? "Debug Mode" : "Debug"}
              </StyledButton>
              <StyledButton
                variant="contained"
                color="warning"
                onClick={() => handleExecuteTest(false)}
                endIcon={<ExportIcon />}
                sx={{
                  backgroundColor: "#ff5336",
                  boxShadow: "0px 16px 30.6px 0px rgba(255, 83, 54, 0.27)",
                }}
              >
                Run
              </StyledButton>
            </Box>
          </Box>
          <Box display="flex" justifyContent={"space-between"} alignItems="center">
            <Box display="flex" columnGap={3} alignItems="center">
              <StudioSelection
                refetchTestCases={refetchTestCases}
                products={products}
                modules={modules}
                testCases={testCases}
                isLoadingTestcase={isLoadingTestcase}
                setTestCaseSearchKeyword={setTestCaseSearchKeyword}
                testCaseSearchKeyword={testCaseSearchKeyword}
                setRefreshFlag={setRefreshFlag}
              />
            </Box>
          </Box>
          {isLoading ? (
            <CircularProgress />
          ) : selectedTestCase && testCaseResults ? (
            <>
              {viewMode === "grid" ? (
                <Box
                  ref={testStepsContainerRef}
                  display="flex"
                  columnGap={4}
                  flexWrap="wrap"
                  rowGap={4}
                  mt={3}
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    minHeight: 0,
                  }}
                >
                  {currentItems.map((result, index) => (
                    <TestItemCard
                      item={result}
                      index={indexOfFirstItem + index}
                      key={result.idtest_step}
                      handleEdit={handleEdit}
                      width={itemWidth}
                      isCheckbox
                      handleCheckboxChange={(e, item) => handleCheckboxChange(e, item)}
                      checkedItems={checkedItems}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  ref={testStepsContainerRef}
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    minHeight: 0,
                  }}
                >
                  <TestTableView
                    testCaseResults={{
                      ...testCaseResults,
                      data: currentItems,
                    }}
                    handleEdit={handleEdit}
                    tsrData={tsrData}
                    isCheckbox
                    handleCheckboxChange={handleCheckboxChange}
                    checkedItems={checkedItems}
                    onInlineTestCaseView={handleInlineTestCaseView}
                  />
                </Box>
              )}
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="300px"
              width="100%"
            >
              No data available
            </Box>
          )}
        </>
      )}
      {currentEditItemIndex !== null && (
        <>
          <EditHeader
            editItem={editData}
            handleAddStep={addStepQuery}
            handleSaveStep={saveStepQuery}
            handleMerge={handleMerge}
          />
          <EditTabs editItem={editData} />
        </>
      )}
    </Box>
  );
};

export default TestStudio;
